import React, { FunctionComponent, useEffect, useCallback } from 'react';

import classnames from 'classnames';
import { UIEvent, PhotoEditorSDKUI, ImageFormat, ExportFormat, Tool } from 'photoeditorsdk';
import { useTranslation } from 'react-i18next';

import { license as photoEditorLicense } from 'common/image-editor/license';
import { Color } from 'core/util/Variables';

import 'common/image-editor/ImageEditor.scss';

export type ImageEditorOutput = 'BLOB' | 'FILE' | 'DOWNLOAD' | 'DATA_URL';

interface Props {
    isActive: boolean;
    src: string;
    outputName?: string;
    outputAs?: ImageEditorOutput;
    onExport?: any;
    onClose?: any;
}

export const ImageEditor: FunctionComponent<Props> = ({
    isActive,
    src,
    outputName,
    outputAs = 'FILE',
    onExport,
    onClose,
}) => {
    const { t } = useTranslation(['common/image-editor']);
    const init = useCallback(async () => {
        let editor;

        if (!src) {
            if (onClose && typeof onClose === 'function') {
                onClose();
            }
            return;
        }

        if (isActive) {
            const now = Date.now();
            const fileOutputName = outputName || `image_file_${String(now)}`;

            const { hostname } = new URL(src);
            let imageUrl = src;

            if (hostname.includes('amazonaws.com')) {
                imageUrl = `https://media.reputation.com/media?u=${src}`;
            }

            editor = await PhotoEditorSDKUI.init({
                license: photoEditorLicense,
                container: '#image-editor',
                theme: 'light',
                image: imageUrl,
                assetBaseUrl: '/rsrc2/images/photoeditorsdk/assets/',
                engine: {
                    crossOrigin: 'anonymous',
                    downscaleOptions: {
                        maxMegaPixels: {
                            desktop: 8,
                            mobile: 4,
                        },
                    },
                },
                displayResizeWarning: true,
                displayCloseWarning: true,
                displayToolControlBarTitle: true,
                enableZoom: true,
                defaultTool: Tool.FILTER,
                tools: [
                    Tool.FILTER,
                    Tool.TRANSFORM,
                    Tool.ADJUSTMENT,
                    Tool.FOCUS,
                    Tool.TEXT,
                    Tool.TEXT_DESIGN,
                    Tool.STICKER,
                    Tool.BRUSH,
                    Tool.FRAME,
                    Tool.OVERLAY,
                ],
                export: {
                    filename: fileOutputName,
                    image: {
                        format: ImageFormat.PNG,
                        exportType: outputAs === 'DATA_URL' ? ExportFormat.DATA_URL : ExportFormat.BLOB,
                        quality: 0.8, // Only if exporting as JPG
                        enableDownload: false || outputAs === 'DOWNLOAD',
                    },
                },
                custom: {
                    languages: {
                        en: {
                            mainCanvasActions: {
                                buttonExport: t('SAVE_IMAGE'),
                                buttonUndo: t('UNDO'),
                                buttonRedo: t('REDO'),
                                buttonClose: t('CANCEL'),
                            },
                            infoModals: {
                                exporting: {
                                    heading: t('EXPORTING'),
                                    body: t('PLEASE_WAIT_ELIPSIS'),
                                },
                                saving: {
                                    heading: t('SAVING'),
                                    body: t('PLEASE_WAIT_ELIPSIS'),
                                },
                                loading: {
                                    heading: t('LOADING'),
                                    body: t('PLEASE_WAIT_ELIPSIS'),
                                },
                                resizing: {
                                    heading: t('RESIZING'),
                                    body: t('PLEASE_WAIT_ELIPSIS'),
                                },
                                loadingFonts: {
                                    heading: t('LOADING_FONTS'),
                                    body: t('PLEASE_WAIT_ELIPSIS'),
                                },
                            },
                            errorModals: {
                                imageLoading: {
                                    headingDelimiter: ':',
                                    body: t('FAILED_TO_LOAD_IMAGE'),
                                    buttonYes: t('RELOAD'),
                                },
                                rendering: {
                                    headingDelimiter: ':',
                                    body: t('ERROR_OCCURED_RENDERING_IMAGE'),
                                    buttonYes: t('RELOAD'),
                                },
                                fontLoading: {
                                    headingDelimiter: ':',
                                    heading: t('FAILED_TO_LOAD_FONT'),
                                    body: t('FOLLOWING_FONTS_COULD_NOT_LOAD'),
                                    buttonNo: t('CLOSE'),
                                },
                                webcamUnavailable: {
                                    headingDelimiter: ':',
                                    body: t('WEBCAM_ERROR'),
                                    buttonYes: t('CLOSE'),
                                },
                                stickerLoading: {
                                    headingDelimiter: ':',
                                    body: t('STICKER_ERROR'),
                                    buttonNo: t('CLOSE'),
                                },
                                unexpectedError: {
                                    headingDelimiter: ':',
                                    body: t('UNEXPECTED_ERROR'),
                                    buttonYes: t('RELOAD'),
                                },
                            },
                            warningModals: {
                                imageResized: {
                                    headingDelimiter: ':',
                                    heading: t('IMAGE_RESIZED'),
                                    body: t('IMAGE_RESIZED_TO_PIXELS'),
                                },
                                unsupportedSerializationVersion: {
                                    headingDelimiter: ':',
                                    body: t('DATA_LEGACY_FORMAT'),
                                    buttonYes: t('APPLY_CHANGES'),
                                    buttonNo: t('CANCEL'),
                                },
                                discardChanges: {
                                    headingDelimiter: ':',
                                    body: t('DISCARD_UNSAVED_CHANGES'),
                                    buttonYes: t('DISCARD_CHANGES'),
                                    buttonNo: t('KEEP_CHANGES'),
                                },
                                unsavedChanges: {
                                    headingDelimiter: ':',
                                    body: t('CLOSE_UNSAVED_CHANGES'),
                                    buttonYes: t('CLOSE_WITHOUT_SAVING'),
                                    buttonNo: t('CANCEL'),
                                },
                                unsupportedWebGLRenderer: {
                                    headingDelimiter: ':',
                                    body: t('PROBLEM_BROWSER'),
                                    buttonYes: t('CONTINUE'),
                                },
                            },
                        },
                    },
                    themes: {
                        light: {
                            button: {
                                /** High Emphasis button - primary variant e.g.: Export button */
                                containedPrimaryBackground: Color.Primary,
                                /** Low Emphasis button - primary variant e.g.: primary button in modal */
                                textPrimaryForeground: Color.Primary,
                                /** Low Emphasis button - secondary variant e.g.: secondary button in modal, undo, redo, Carousel navigators, alignment buttons */
                                // textSecondaryForeground: 'rgba(255, 255, 255, 0.90)',
                            },
                        },
                    },
                    measurements: {
                        fontSystem: {
                            headline1: {
                                case: 'none',
                                letterSpacing: 0,
                            },
                            headline2: {
                                case: 'none',
                                letterSpacing: 0,
                            },
                            body: {
                                case: 'none',
                                letterSpacing: 0,
                            },
                            label: {
                                case: 'none',
                                letterSpacing: 0,
                            },
                            basicUICardLabel: {
                                case: 'none',
                                letterSpacing: 0,
                            },
                            button1: {
                                case: 'none',
                                size: 14,
                                letterSpacing: 0,
                            },
                            button2: {
                                case: 'none',
                                letterSpacing: 0,
                            },
                            basicUITabLabel: {
                                case: 'none',
                                letterSpacing: 0,
                            },
                        },
                    },
                },
            });

            /* Editor Events */
            editor.on(UIEvent.EXPORT, (blob: any) => {
                let output;

                if (outputAs === 'FILE' || outputAs === 'DOWNLOAD') {
                    output = new File([blob], `${fileOutputName}.png`, { type: 'image/png' });
                } else if (outputAs === 'BLOB' || outputAs === 'DATA_URL') {
                    output = blob;
                }

                if (onExport && typeof onExport === 'function') {
                    onExport(output);
                }
                if (onClose && typeof onClose === 'function') {
                    onClose();
                }
            });

            editor.on(UIEvent.CLOSE, () => {
                if (onClose && typeof onClose === 'function') {
                    onClose();
                }
            });

            editor.on(UIEvent.ERROR_IMAGE_LOADING, (err: any) => {
                if (onClose && typeof onClose === 'function') {
                    onClose(err);
                }
            });
        }
    }, [t, isActive, src, outputName, outputAs, onExport, onClose]);

    useEffect(() => {
        init();
    }, [init]);

    const editorClassName = classnames('r4e-image-editor', {
        active: isActive,
        inactive: !isActive,
    });

    return <section className={editorClassName}>{isActive ? <div id="image-editor" /> : <></>}</section>;
};
